import { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
} from "@mui/material";

import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const LocationUpdateImport = ({ open, onClose, onQueued }) => {
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const email = useSelector((state) => state.authentication.email);

  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const resetState = () => {
    setSelectedFile(null);
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    resetState();
    onClose?.();
  };

  const readCsvText = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const utf8Text = new TextDecoder("utf-8", { fatal: false }).decode(
      arrayBuffer,
    );

    if (!utf8Text.includes("�")) return utf8Text;

    return new TextDecoder("windows-1252", { fatal: false }).decode(
      arrayBuffer,
    );
  };

  const handleStart = async () => {
    if (!selectedFile) {
      showMessage("Choose a CSV exported from Locations first", "warning");
      return;
    }

    setLoading(true);

    try {
      const csvData = await readCsvText(selectedFile);

      const results = await executeAction({
        entityName: "Location",
        action: "importUpdates",
        data: {
          csvData,
          email,
        },
      });

      if (!results?.isSuccessful) {
        showMessage(
          results?.message || "Unable to queue update import",
          "error",
        );
        setLoading(false);
        return;
      }

      showMessage(
        "Location update import queued. You will receive an email summary.",
        "success",
      );

      onQueued?.(results?.data?.workQueueId || null);
      handleClose();
    } catch (error) {
      showMessage(`Unable to queue update import: ${error.message}`, "error");
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Update Existing Locations</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography
            variant="body2"
            color="textSecondary"
          >
            Upload a CSV exported from Locations. This import updates existing
            locations by LocationId only.
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
          >
            Updatable fields: Name, Location#, Status, Format, Street Address,
            Other, City, Province/Prov, Postal, Latitude, Longitude.
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
          >
            Banner and other columns are read-only and ignored.
          </Typography>

          <input
            type="file"
            accept=".csv"
            onChange={(event) => {
              const file = event.target.files?.[0] || null;
              setSelectedFile(file);
            }}
            disabled={loading}
          />

          {selectedFile ? (
            <Typography
              variant="caption"
              color="textSecondary"
            >
              Selected: {selectedFile.name}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleStart}
          disabled={loading || !selectedFile}
        >
          {loading ? <CircularProgress size={20} /> : "Start Import"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationUpdateImport;
